import { X } from "@phosphor-icons/react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@stylingcv/utils";
import { AnimatePresence, motion, useDragControls, type PanInfo } from "framer-motion";
import { forwardRef, useCallback, useState } from "react";

export const Dialog = DialogPrimitive.Root;

export const DialogTrigger = DialogPrimitive.Trigger;

export const DialogPortal = (props: DialogPrimitive.DialogPortalProps) => (
  <DialogPrimitive.Portal {...props} />
);

DialogPortal.displayName = DialogPrimitive.Portal.displayName;

export const DialogOverlay = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));

DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export const DialogContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-1/2 top-1/2 z-[9999] grid max-h-[90vh] w-[calc(100vw-2rem)] max-w-[90vw] -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto rounded-2xl border-0 bg-white p-6 shadow-2xl duration-200 focus:outline-none dark:bg-gray-900 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl",
        className,
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 disabled:pointer-events-none dark:hover:bg-gray-800 dark:hover:text-gray-200 rtl:right-auto rtl:left-4">
        <X className="size-5" />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));

DialogContent.displayName = DialogPrimitive.Content.displayName;

/**
 * MobileDialogContent - Bottom sheet dialog for mobile devices
 * Slides up from the bottom with swipe-to-dismiss gesture
 */
type MobileDialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  onClose?: () => void;
};

export const MobileDialogContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  MobileDialogContentProps
>(({ className, children, onClose, ...props }, ref) => {
  const [dragY, setDragY] = useState(0);

  const handleDragEnd = useCallback(
    (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      // If user swiped down more than 80px, close the dialog
      if (info.offset.y > 80) {
        onClose?.();
      }
      setDragY(0);
    },
    [onClose],
  );

  return (
    <DialogPortal>
      <DialogOverlay style={{ opacity: Math.max(0.3, 1 - dragY / 200) }} />
      <DialogPrimitive.Content ref={ref} asChild {...props}>
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          drag="y"
          dragConstraints={{ top: 0, bottom: 0 }}
          dragElastic={{ top: 0, bottom: 0.3 }}
          onDrag={(_, info) => setDragY(Math.max(0, info.offset.y))}
          onDragEnd={handleDragEnd}
          style={{ y: dragY }}
          className={cn(
            // Bottom sheet positioning - fixed to bottom, full width on mobile
            "fixed inset-x-0 bottom-0 z-[9999]",
            // Size and shape
            "max-h-[92vh] w-full rounded-t-2xl",
            // Appearance
            "border-0 bg-[#faf9f5] shadow-2xl focus:outline-none dark:bg-gray-900",
            // Content layout
            "flex flex-col overflow-hidden",
            // Touch handling
            "touch-manipulation",
            className,
          )}
        >
          {/* Swipe indicator handle */}
          <div className="flex shrink-0 justify-center py-3">
            <div className="h-1.5 w-12 rounded-full bg-gray-300 dark:bg-gray-600" />
          </div>
          
          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto px-6 pb-safe">
            {children}
          </div>

          {/* Close button */}
          <DialogPrimitive.Close className="absolute right-4 top-4 rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 disabled:pointer-events-none dark:hover:bg-gray-800 dark:hover:text-gray-200 rtl:left-4 rtl:right-auto">
            <X className="size-5" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        </motion.div>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});

MobileDialogContent.displayName = "MobileDialogContent";

type DialogHeaderProps = React.HTMLAttributes<HTMLDivElement> & {
  bordered?: boolean;
};

export const DialogHeader = ({ className, bordered = true, ...props }: DialogHeaderProps) => (
  <div
    className={cn(
      "flex flex-col space-y-2 pb-4 text-left",
      bordered && "mb-2 border-b border-gray-100 pb-4 dark:border-gray-800",
      className,
    )}
    {...props}
  />
);

DialogHeader.displayName = "DialogHeader";

type DialogFooterProps = React.HTMLAttributes<HTMLDivElement> & {
  bordered?: boolean;
};

export const DialogFooter = ({ className, bordered = true, ...props }: DialogFooterProps) => (
  <div
    className={cn(
      "flex flex-col-reverse gap-3 pt-4 sm:flex-row sm:items-center sm:justify-end",
      bordered && "mt-2 border-t border-gray-100 pt-4 dark:border-gray-800",
      className,
    )}
    {...props}
  />
);

DialogFooter.displayName = "DialogFooter";

export const DialogTitle = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-bold leading-none tracking-tight text-gray-900 dark:text-white", className)}
    {...props}
  />
));

DialogTitle.displayName = DialogPrimitive.Title.displayName;

export const DialogDescription = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm leading-relaxed text-gray-500 dark:text-gray-400", className)}
    {...props}
  />
));

DialogDescription.displayName = DialogPrimitive.Description.displayName;

// ============================================
// NEW COMPOSITIONAL COMPONENTS
// ============================================

/**
 * DialogIconHeader - Premium header with icon, title, and description
 * Used for beautiful modal headers like the cover letter generator
 */
type DialogIconHeaderProps = {
  icon: React.ReactNode;
  iconClassName?: string;
  title: string;
  description?: string;
  className?: string;
};

export const DialogIconHeader = ({
  icon,
  iconClassName,
  title,
  description,
  className,
}: DialogIconHeaderProps) => (
  <div
    className={cn(
      "flex items-center justify-between border-b border-gray-100 px-6 py-4 dark:border-gray-800",
      className,
    )}
  >
    <div className="flex items-center gap-3">
      <div
        className={cn(
          "flex size-10 items-center justify-center rounded-xl",
          iconClassName || "bg-gradient-to-br from-purple-500 to-pink-500",
        )}
      >
        {icon}
      </div>
      <div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">{title}</h2>
        {description && (
          <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
        )}
      </div>
    </div>
  </div>
);

DialogIconHeader.displayName = "DialogIconHeader";

/**
 * DialogBody - Scrollable content area with proper padding
 */
type DialogBodyProps = React.HTMLAttributes<HTMLDivElement>;

export const DialogBody = ({ className, ...props }: DialogBodyProps) => (
  <div
    className={cn("max-h-[calc(90vh-180px)] space-y-5 overflow-y-auto p-6", className)}
    {...props}
  />
);

DialogBody.displayName = "DialogBody";

/**
 * DialogInfoBanner - Colored info/tip boxes
 */
type DialogInfoBannerProps = React.HTMLAttributes<HTMLDivElement> & {
  variant?: "info" | "warning" | "success" | "purple" | "amber";
  icon?: React.ReactNode;
};

const bannerVariants = {
  info: "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300",
  warning: "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300",
  success: "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300",
  purple: "bg-gradient-to-r from-purple-50 to-pink-50 text-purple-900 dark:from-purple-900/20 dark:to-pink-900/20 dark:text-purple-100",
  amber: "bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200",
};

export const DialogInfoBanner = ({
  variant = "info",
  icon,
  className,
  children,
  ...props
}: DialogInfoBannerProps) => (
  <div
    className={cn("flex items-start gap-3 rounded-lg p-4", bannerVariants[variant], className)}
    {...props}
  >
    {icon && <span className="mt-0.5 shrink-0">{icon}</span>}
    <div className="min-w-0 flex-1 text-sm">{children}</div>
  </div>
);

DialogInfoBanner.displayName = "DialogInfoBanner";

/**
 * DialogActions - Footer actions with token cost display
 */
type DialogActionsProps = React.HTMLAttributes<HTMLDivElement> & {
  tokenCost?: number;
  tokenIcon?: React.ReactNode;
};

export const DialogActions = ({
  tokenCost,
  tokenIcon,
  className,
  children,
  ...props
}: DialogActionsProps) => (
  <div
    className={cn(
      "flex items-center justify-between border-t border-gray-100 px-6 py-4 dark:border-gray-800",
      className,
    )}
    {...props}
  >
    {tokenCost !== undefined ? (
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
        {tokenIcon}
        <span>Costs {tokenCost} tokens</span>
      </div>
    ) : (
      <div />
    )}
    <div className="flex gap-3">{children}</div>
  </div>
);

DialogActions.displayName = "DialogActions";
